import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';

import { Transport } from '../../../../models/transport.model';
import { PATHS } from '../../../../models/bary-paths.model';
import { EstimationStatus, LastEstimate } from '../../../../models/estimate-result-bundle.model';
import { IBaryError } from '../../../../models/bary-error';
import { Transporter } from '../../../../models/transporter.model';
import { OfferViewDTO } from '../../../../models/offer-view-dto.type';
import { PricingOption } from '../../../../models/pricing-option.model';
import { TransportStep } from '../../../../models/enums/transport-step.enum';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { PricingOptionType } from '../../../../models/enums/pricing-option-type.enum';
import { AddToBasket } from '../../../../models/add-to-basket.model';
import { AddToBasketStatus } from '../../../../models/add-to-basket-status.model';
import { TUNNEL_STEPS } from '../../../../models/constants/tunnel-steps.const';
import { TunnelStep } from '../../../../models/tunnel-step.model';

import { EstimationTunnelService } from '../../../../services/estimation-tunnel.service';
import { LoginModalService } from '../../../../services/login-modal.service';
import { OfferService } from '../../../../services/offer.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { BaryErrorService } from '../../../../services/bary-error-service';
import { TransportDateService } from '../../../../services/transport-date-service';

import dayjs from 'dayjs';
import { PricingTransporterConfigurationService } from '../../../../services/pricing-transporter-configuration.service';
import { mapAsBody } from '../../../shared/util/request-util';

@Component({
  selector: 'jhi-estimation-tunnel',
  templateUrl: './estimation-tunnel.component.html',
})
export class EstimationTunnelComponent implements OnInit {
  readonly #currentDataService = inject(CurrentDataService);
  readonly #bff = inject(BaryFormFactoryService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #translateService = inject(TranslateService);
  readonly #modalService = inject(BaryModalService);
  readonly #baryErrorService = inject(BaryErrorService);
  readonly #estimationTunnelService = inject(EstimationTunnelService);
  readonly #loginModalService = inject(LoginModalService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #offerService = inject(OfferService);
  readonly #transportDateService = inject(TransportDateService);
  readonly #pricingTransporterConfigurationService = inject(PricingTransporterConfigurationService);

  public readonly STEPS = TUNNEL_STEPS;
  public activeIndex = 0;

  public transport!: Transport;
  public transportForm!: TransportFormGroup;
  public transporter!: Transporter;
  public lastEstimate?: LastEstimate;
  public lastValidation?: LastEstimate;

  public offer!: OfferViewDTO;

  public loadingData = true;
  public isAuthenticated = false;

  public errorMessages: string[] = [];
  public quotationMessages: string[] = [];
  public insuranceIncluded: boolean = false;
  public optionInsurance?: PricingOption;
  public validationResult: number[] = [];

  ngOnInit(): void {
    this.#spinnerService.show().then();
    forkJoin({
      data: this.#route.data.pipe(take(1)),
      customer: this.#currentDataService.getCustomer().pipe(take(1)),
      config: this.#pricingTransporterConfigurationService.retrieveConfiguration().pipe(mapAsBody),
    }).subscribe({
      next: response => {
        this.transport = response.data['transport'];
        this.transporter = response.data['transporter'];
        this.isAuthenticated = !!response.customer;
        this.transportForm = this.#bff.makeTransportForm(response.config, this.transporter, this.transport);
        this.addExtraConditionStep();
        this.loadOfferDataFromTransport(this.transport);
        this.initListeners();
      },
    });

    this.#router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(event => {
      if (event instanceof NavigationStart && event.navigationTrigger === 'popstate') {
        this.activeIndex = this.STEPS.findIndex(it => it.url === event.url);
      }
    });
  }

  public initListeners(): void {
    combineLatest([
      this.transportForm.controls.itemsNature.valueChanges.pipe(startWith(this.transportForm.controls.itemsNature.value), distinctUntilChanged()),
      this.transportForm.controls.goodsValue.valueChanges.pipe(startWith(this.transportForm.controls.goodsValue.value), distinctUntilChanged()),
      this.transportForm.controls.euroPallet.valueChanges.pipe(startWith(this.transportForm.controls.euroPallet.value), distinctUntilChanged()),
      this.transportForm.controls.pickupLoadingConstraint.valueChanges.pipe(startWith(this.transportForm.controls.pickupLoadingConstraint.value), distinctUntilChanged()),
      this.transportForm.controls.pickupAccessConstraint.valueChanges.pipe(startWith(this.transportForm.controls.pickupAccessConstraint.value), distinctUntilChanged()),
      this.transportForm.controls.offerId?.valueChanges.pipe(startWith(this.transportForm.controls.offerId?.value), distinctUntilChanged()),
      this.transportForm.controls.merchandiseType?.valueChanges.pipe(startWith(this.transportForm.controls.merchandiseType?.value), distinctUntilChanged()),
      this.transportForm.controls.serviceType?.valueChanges.pipe(startWith(this.transportForm.controls.serviceType?.value), distinctUntilChanged()),
      this.transportForm.controls.isControlledTemperature?.valueChanges.pipe(startWith(this.transportForm.controls.isControlledTemperature?.value), distinctUntilChanged()),
      this.transportForm.controls.isHazardousMaterial?.valueChanges.pipe(startWith(this.transportForm.controls.isHazardousMaterial?.value), distinctUntilChanged()),
      //Pickup
      this.transportForm.controls.pickupMeeting?.valueChanges.pipe(startWith(this.transportForm.controls.pickupMeeting?.value), distinctUntilChanged()),
      this.transportForm.controls.pickupContact
        ?.getAngularForm()
        .controls['phone']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupContact
        ?.getAngularForm()
        .controls['lastName']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupContact
        ?.getAngularForm()
        .controls['firstName']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupAddress
        ?.getAngularForm()
        .controls['city']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupAddress
        ?.getAngularForm()
        .controls['cityCode']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupAddress
        ?.getAngularForm()
        .controls['country']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.pickupAddress
        ?.getAngularForm()
        .controls['zipCode']?.valueChanges.pipe(startWith(this.transportForm.controls.pickupAddress?.value), distinctUntilChanged(), debounceTime(500)),
      //Delivery
      this.transportForm.controls.deliveryMeeting?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryMeeting?.value), distinctUntilChanged()),
      this.transportForm.controls.deliveryContact
        ?.getAngularForm()
        .controls['phone']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryContact
        ?.getAngularForm()
        .controls['firstName']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryContact
        ?.getAngularForm()
        .controls['lastName']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryContact?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryAddress
        ?.getAngularForm()
        .controls['city']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryAddress
        ?.getAngularForm()
        .controls['cityCode']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryAddress
        ?.getAngularForm()
        .controls['country']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryAddress?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryAddress
        ?.getAngularForm()
        .controls['zipCode']?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryAddress?.value), distinctUntilChanged(), debounceTime(500)),

      this.transportForm.controls.pickupTimeslotStart?.valueChanges.pipe(
        startWith(this.transportForm.controls.pickupTimeslotStart?.value),
        distinctUntilChanged(),
        debounceTime(500)
      ),
      this.transportForm.controls.pickupTimeslotEnd?.valueChanges.pipe(startWith(this.transportForm.controls.pickupTimeslotEnd?.value), distinctUntilChanged(), debounceTime(500)),
      this.transportForm.controls.deliveryTimeslotStart?.valueChanges.pipe(
        startWith(this.transportForm.controls.deliveryTimeslotStart?.value),
        distinctUntilChanged(),
        debounceTime(500)
      ),
      this.transportForm.controls.deliveryTimeslotEnd?.valueChanges.pipe(
        startWith(this.transportForm.controls.deliveryTimeslotEnd?.value),
        distinctUntilChanged(),
        debounceTime(500)
      ),
      this.transportForm.controls.itemsCondition?.valueChanges.pipe(startWith(this.transportForm.controls.itemsCondition?.value), distinctUntilChanged()),
      this.transportForm.controls.pickupRegisteredAddressId?.valueChanges.pipe(startWith(this.transportForm.controls.pickupRegisteredAddressId?.value), distinctUntilChanged()),
      this.transportForm.controls.deliveryRegisteredAddressId?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryRegisteredAddressId?.value), distinctUntilChanged()),
      this.transportForm.controls.pickupDate?.valueChanges.pipe(startWith(this.transportForm.controls.pickupDate?.value), distinctUntilChanged()),
      this.transportForm.controls.deliveryDate?.valueChanges.pipe(startWith(this.transportForm.controls.deliveryDate?.value), distinctUntilChanged()),
    ])
      .pipe(debounceTime(500))
      .subscribe((): void => {
        this.transport = new Transport({ ...this.transport.serialize(), ...this.transportForm.getAngularForm().getRawValue() });
        if (this.transport.pickupDate?.isAfter(this.transport?.deliveryDate)) {
          this.#transportDateService.getNextDeliveryDayAvailable(this.transport).subscribe((minDate: string) => {
            this.transportForm.controls.deliveryDate.setValue(dayjs(minDate));
          });
        } else {
          this.refreshPrice();
        }
      });

    //Precheck Validation
    this.checkValidation();
    this.transportForm.getAngularForm().valueChanges.subscribe(() => this.checkValidation());
  }

  private addExtraConditionStep(): void {
    if (this.transport.transportExtraConditions && (this.transport.isHazardousMaterial || this.transport.isControlledTemperature) && this.STEPS.length === 3) {
      this.STEPS[2].hasNextStep = true;
      this.STEPS.push(
        new TunnelStep({
          title: 'ESTIMATION_TUNNEL.STEP.EXTRA_CONDITION',
          url: PATHS.ESTIMATE_EXTRA_CONDITION.path,
          hasNextStep: false,
          step: TransportStep.EXTRA_CONDITION,
          icon: 'clipboard',
        })
      );
    } else if ((!this.transport.transportExtraConditions || (!this.transport.isHazardousMaterial && !this.transport.isControlledTemperature)) && this.STEPS.length === 4) {
      this.STEPS[2].hasNextStep = false;
      this.STEPS.pop();
    }
  }

  /**
   * This methods will get transport offer data and use some of them for setting up some form behavior
   *
   * @private
   */
  public loadOfferDataFromTransport(transport: Transport): void {
    if (transport?.offerId) {
      this.#offerService.getTransportOffer(transport.offerId).subscribe((value: OfferViewDTO): void => {
        this.offer = value;
        this.optionInsurance = (this.offer.pricingOptions || []).find(option => option.type === PricingOptionType.INCLUDED_INSURANCE && !!option.value);
        this.insuranceIncluded = !!this.optionInsurance;
      });
    }
  }

  public onActivate(component: any): void {
    component.transport = this.transport;
    component.transportForm = this.transportForm;
    component.transporter = this.transporter;
    component.isAuthenticated = this.isAuthenticated;
    if (this.insuranceIncluded) {
      component.insuranceIncluded = this.insuranceIncluded;
      component.optionInsurance = this.optionInsurance;
    }
  }

  public cancel(): void {
    this.#estimationTunnelService.doCancel();
  }

  public checkValidation(): void {
    this.errorMessages = [];
    this.quotationMessages = [];
    this.validationResult = this.#estimationTunnelService.precheckValidateStep(this.activeIndex, this.transportForm);
    this.STEPS[this.activeIndex].invalid = this.validationResult.length !== 0;
  }

  /**
   * Mise à jour du tarif :
   * On met à jour le tarif en faisant un appel serveur.
   * Puis, on met à jour le transport avec les données de retour.
   */
  public refreshPrice(): void {
    this.#estimationTunnelService
      .refreshPrice(this.transport, undefined)
      .pipe(take(1))
      .subscribe(lastEstimate => {
        this.errorMessages = lastEstimate.errorMessages;
        this.quotationMessages = lastEstimate.quotationMessages;
        this.lastEstimate = lastEstimate;
      })
      .add((): void => {
        this.transportForm.getAngularForm().markAllAsTouched();
        this.loadingData = false;
        this.#spinnerService.hide().then();
      });
  }

  public next(validation?: boolean): void {
    this.STEPS.forEach(it => (it.invalid = false));
    this.validationResult = this.#estimationTunnelService.precheckValidateStep(this.activeIndex, this.transportForm);
    if (this.validationResult.length === 0) {
      this.#spinnerService.show().then();
      this.transport = new Transport({ ...this.transport.serialize(), ...this.transportForm.getAngularForm().getRawValue() });
      this.#estimationTunnelService
        .refreshPrice(this.transport, undefined)
        .pipe(take(1))
        .subscribe(lastEstimate => {
          this.errorMessages = lastEstimate.errorMessages;
          this.quotationMessages = lastEstimate.quotationMessages;
          this.lastEstimate = lastEstimate;
          if (validation) {
            this.validate(this.transport);
          } else {
            this.activeIndex++;
            this.#router.navigateByUrl(this.STEPS[this.activeIndex].url).then();
          }
        })
        .add(() => {
          this.loadingData = false;
        });
    } else {
      this.validationResult.forEach(stepNumber => {
        this.STEPS[stepNumber].invalid = true;
      });
      this.#spinnerService.hide().then();
    }
  }

  public previous(): void {
    this.#spinnerService.show().then();
    this.activeIndex--;
    this.#router.navigateByUrl(this.STEPS[this.activeIndex].url).then(() => this.#spinnerService.hide());
  }

  public processAddToBasketResult(result: AddToBasket, validatedTransport: Transport): Observable<any> {
    this.errorMessages = [];
    switch (result.status) {
      case AddToBasketStatus.Failed: {
        this.errorMessages = this.#baryErrorService.baryErrorsToString(result.failedRuleKeys as IBaryError[]);
        this.lastEstimate?.updateStatus(EstimationStatus.ERROR);
        break;
      }
      case AddToBasketStatus.FailedButEstimate: {
        this.quotationMessages = this.#baryErrorService.baryErrorsToString(result.failedRuleKeys as IBaryError[]);
        this.lastEstimate?.updateStatus(EstimationStatus.QUOTATION);
        break;
      }
      case AddToBasketStatus.NoUserFound: {
        this.errorMessages = this.#translateService.instant('ERROR.INACTIVE_SESSION');
        this.lastEstimate?.updateStatus(EstimationStatus.ERROR);
        break;
      }
      case AddToBasketStatus.Succeeded: {
        if (this.lastEstimate?.status === EstimationStatus.QUOTATION) {
          this.#currentDataService.removeCurrentTransport();
          this.#currentDataService.removeTransportInModification();
          const message = this.#translateService.instant('ESTIMATION_TUNNEL.MESSAGE.QUOTATION_CONFIRMED');
          this.#spinnerService.hide().then();
          this.#modalService
            .openSuccess(message)
            .action()
            .subscribe(() => {
              this.#router.navigateByUrl(PATHS.USER_MY_QUOTATION_NEW.path).then();
            });
        } else {
          this.#currentDataService.updateCurrentTransport(validatedTransport);
          this.#currentDataService.removeTransportInModification();
          this.#router.navigateByUrl(PATHS.ORDER_BASKET.path).then();
        }
        break;
      }
    }
    return of();
  }

  public validate(validatedTransport: Transport): void {
    this.#estimationTunnelService
      .validateStep(validatedTransport, TransportStep.ITEMS)
      .pipe(
        map(estimation => {
          const hasChange = this.lastEstimate?.status !== estimation.status && estimation.status !== 'OK';
          this.lastValidation = estimation;
          this.lastEstimate = estimation;
          this.errorMessages = estimation.errorMessages;
          this.quotationMessages = estimation.quotationMessages;
          return hasChange;
        })
      )
      .subscribe(hasChange => {
        if (!hasChange) {
          this.#currentDataService.getCustomer().subscribe(customer => {
            if (customer) {
              this.estimatesCases(validatedTransport);
            } else {
              this.tryToLogin(validatedTransport);
            }
          });
        }
      });
  }

  private tryToLogin(validatedTransport: Transport): void {
    this.#estimationTunnelService.saveInLocalStorage(validatedTransport);
    const description = this.#translateService.instant('ESTIMATION_TUNNEL.MESSAGE.LOGIN_REQUIRED');
    this.#spinnerService.hide().then();
    this.#loginModalService.open(false, description);
  }

  private estimatesCases(validatedTransport: Transport): void {
    switch (this.lastEstimate?.status) {
      case EstimationStatus.OK: {
        if (this.transport.id) {
          // Il s'agit d'une modification d'un transport déjà présent dans le panier
          this.#estimationTunnelService.doUpdateTransportInBasket(validatedTransport).subscribe(addToBasket => this.processAddToBasketResult(addToBasket, validatedTransport));
        } else {
          // Il s'agit d'un nouveau transport à ajouter au panier
          this.#estimationTunnelService.doAddToBasket(validatedTransport).subscribe(addToBasket => this.processAddToBasketResult(addToBasket, validatedTransport));
        }
        break;
      }
      case EstimationStatus.QUOTATION: {
        this.#estimationTunnelService.doQuotationRequest(validatedTransport).subscribe(addToBasket => this.processAddToBasketResult(addToBasket, validatedTransport));
        break;
      }
      case EstimationStatus.ERROR: {
        console.error('Incorrect access to this method');
        break;
      }
    }
  }
}
